/**
 * LessonBookingAdjustmentModal — Adapter wrapper around the existing ModalContainer.
 *
 * Allows administrators to adjust booked lessons of a single settled lesson booking
 * directly from the booking details page (without going through the CourseEdit flow).
 *
 * Architecture:
 * - Loads fresh course data via useGetCourseByIdQuery({fetchPolicy: "network-only"}).
 * - Reuses prepareInitialValues (exported from CourseEditForm) to build the EditCourseInput shape.
 * - Wraps everything in a Formik with a single-element bookingsOfCourse array.
 * - Renders ModalContainer with hideSelector={true} to suppress the Vorherige/Nächste navigation.
 * - On OK: calls processFormData with three arguments (values, courseId, data.courseById)
 *   so includedEntranceFee + maxAssistantsPerLesson + customInvoiceDueDays are preserved.
 *
 * Shape note (Rev 3 R1 / A.6):
 * Course-Daten werden direkt als data.courseById übergeben. processFormData liest
 * coursePrice (singular, GetCourseByIdQuery) ?? coursePrices (plural, EditCourseInput)
 * als Fallback (siehe helper.tsx Task A.5 Teil 2). Keine künstliche Shape-Konversion nötig.
 */
import React, {FC, useRef} from "react";
import {Modal, Button, message} from "antd";
import {Formik, FormikProps} from "formik";
import i18n from "../../../services/i18n";
import {
    useGetCourseByIdQuery,
    useUpdateCourseAndBookingsMutation,
    GetCourseByIdDocument,
    GetCourseLessonsByCourseIdDocument,
    GetBookingsByCourseIdWithCourseLessonBookingsOptimizedDocument,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetBookingByIdWithCourseLessonBookingsQuery,
    GetBookingsByCourseDocument,
    HistorySource,
    UpdateCourseAndBookingsInput,
} from "../../../generated/graphql";
import ModalContainer from "../../courses/LessonBookings/ModalContainer";
import {prepareInitialValues} from "../../courses/CourseEdit/CourseEditForm";
import {processFormData, getNewestEntry} from "../../courses/LessonBookings/helper";
import {showBillingLockErrorModal} from "../../courses/helper/billingLockHelpers";
import {PossibleFormikPropsType} from "../../courses/CourseCreate/CourseCreateForm";
import LoadingContent from "../../../views/LoadingContent/LoadingContent";

type BookingForAdjustment = GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type LessonBookingAdjustmentModalProps = {
    open: boolean;
    onClose: () => void;
    booking: BookingForAdjustment;
    onSuccess?: () => void;
};

const LessonBookingAdjustmentModal: FC<LessonBookingAdjustmentModalProps> = ({open, onClose, booking, onSuccess}) => {
    // courseId stammt direkt vom Booking-Objekt (ist im GetBookingByIdWithCourseLessonBookings-Selection-Set).
    // Fallback ueber die courseLessons-Relation, falls jemand spaeter ein abweichendes Booking-Type uebergibt.
    const courseId =
        ((booking as unknown) as {courseId?: string}).courseId ??
        booking?.courseLessons?.[0]?.course?.id ??
        undefined;

    const formikRef = useRef<FormikProps<PossibleFormikPropsType> | null>(null);

    const {data, loading, error} = useGetCourseByIdQuery({
        variables: {id: courseId as string},
        fetchPolicy: "network-only",
        skip: !open || !courseId,
    });

    const refetchQueries = [
        {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: booking?.id}},
        {query: GetCourseByIdDocument, variables: {id: courseId}},
        {query: GetCourseLessonsByCourseIdDocument, variables: {courseId}},
        {query: GetBookingsByCourseIdWithCourseLessonBookingsOptimizedDocument, variables: {courseId}},
        // Review K2: GetBookingsByCourse hat Variables `id` (courseId), siehe generated/graphql.tsx:9750.
        {query: GetBookingsByCourseDocument, variables: {id: courseId}},
        // UAT-Fix 2026-05-04: Posten-Listen in PanelCardPayments/OpenPaymentsList/DonePaymentsList
        // nutzen separate Queries mit eigenen Variables (bookingId, options, includeDeleted).
        // Operation-Name-Strings refetchen alle aktiven Instanzen unabhaengig von den Variables —
        // ohne dieser Eintraege zeigen die Tabellen veraltete Raten bis zum manuellen Reload.
        "getUnpaidScheduledPayments",
        "getPaidScheduledPayments",
        // Booking-History soll den neuen Eintrag sofort zeigen (Drawer und Inline).
        "BookingHistoryByIdWithBookingInfo",
        "BookingHistoryByBookingId",
        // GetCourseDetailsByIdDocument NICHT in refetchQueries:
        //   Die Detail-Seiten verwenden bereits network-only/no-cache
        //   (SettledLessonBookingDetails.tsx, CourseDetailsLessonBooking.tsx).
    ];

    const [updateCourseAndBookings, {loading: mutationLoading}] = useUpdateCourseAndBookingsMutation({refetchQueries});

    const handleConfirm = async () => {
        if (!formikRef.current || !courseId || !data?.courseById) {
            message.error("Formular nicht bereit");

            return;
        }
        const liveValues = formikRef.current.values as UpdateCourseAndBookingsInput;
        // Review K1 + Rev 3 R1: Dritter Parameter MUSS uebergeben werden, damit
        //   - includedEntranceFee aus den echten Course-Daten gelesen wird (nicht null)
        //   - maxAssistantsPerLesson und customInvoiceDueDays korrekt befuellt werden (Task A.5)
        //   - der Shape-Fallback coursePrice ?? coursePrices in helper.tsx greift (Task A.5 Teil 2)
        const {editCourseData} = processFormData(liveValues, courseId, data.courseById as never);
        try {
            await updateCourseAndBookings({
                variables: {
                    dryRun: false,
                    updateCourseAndBookingsInput: {
                        ...editCourseData,
                        courseId,
                        updatedBookingsData: liveValues.updatedBookingsData,
                        // Phase 15.2 (H3): explizite Quellen-Markierung — semantisch ein
                        // Single-Booking-Edit aus dem Buchungsdetail-Modal. Unterdrückt
                        // im Backend das Suffix " (durch Kursanpassung)" in History-Einträgen.
                        // Siehe .planning/phases/15.2-detailed-payment-history-events/15.2-01-PLAN.md §2.5.
                        historySource: HistorySource.BookingAdjustment,
                    },
                },
                fetchPolicy: "no-cache",
            });
            message.success(i18n.containers.invoice.SettlementDetails.adjustModal.success());
            onSuccess?.();
            onClose();
        } catch (err) {
            // Spiegelt CourseEditForm.tsx:489-531 — Modal bleibt offen, onClose NICHT aufrufen.
            const errorMessage =
                ((err as unknown) as {graphQLErrors?: Array<{message?: string}>; message?: string})?.graphQLErrors?.[0]?.message ??
                ((err as unknown) as {message?: string})?.message ??
                "Unbekannter Fehler";

            // Branch 1 (Rev 3 R3): Billing-Lock — der wahrscheinlichste Fehler beim Editieren
            //   abgerechneter Buchungen. Reuse des bestehenden Helpers aus billingLockHelpers.tsx.
            if (errorMessage.includes("abgerechnete Kurseinheiten") || errorMessage.includes("bereits abgerechnet")) {
                showBillingLockErrorModal(errorMessage);

                return;
            }

            // Branch 2 (Rev 4 MEDIUM): Concurrent-Update-Konflikt — Marker-Set als Superset der CourseEdit-Baseline.
            //   CourseEditForm.tsx:488 matched: "transaction is aborted", "serialization conflict", "duplicate key".
            //   Plus optional "could not serialize access" (PostgreSQL-Originaltext).
            if (
                errorMessage.includes("transaction is aborted") ||
                errorMessage.includes("serialization conflict") ||
                errorMessage.includes("duplicate key") ||
                errorMessage.includes("could not serialize access")
            ) {
                Modal.error({
                    title: i18n.containers.invoice.SettlementDetails.adjustModal.error.title(),
                    content: "Ein anderer Benutzer hat den Kurs gleichzeitig geändert. Bitte schließe das Modal, lade die Seite neu und versuche es erneut.",
                    okText: "Verstanden",
                    width: 500,
                });

                return;
            }

            // Branch 3: Generischer Fallback
            Modal.error({
                title: i18n.containers.invoice.SettlementDetails.adjustModal.error.title(),
                content: errorMessage,
                okText: "Verstanden",
                width: 500,
            });
        }
    };

    if (!booking) {
        return null;
    }

    const modalTitle = i18n.containers.invoice.SettlementDetails.adjustModal.title({
        bookingNumber: `${booking.bookingNumberPrefix}-${booking.bookingNumber}`,
    });

    // Build initialValues only when course data is available
    let initialValues: PossibleFormikPropsType | null = null;
    let bookingsArray: Parameters<typeof prepareInitialValues>[2] = undefined;

    if (data?.courseById) {
        const course = data.courseById as unknown as {prefixedCourseNumber?: string} & Record<string, unknown>;
        const {prefixedCourseNumber: _ignored, ...initialCourseValues} = course;
        const latestPriceData = getNewestEntry((initialCourseValues as {coursePrice?: Parameters<typeof getNewestEntry>[0]}).coursePrice);
        // booking aus props ist GetBookingByIdWithCourseLessonBookings — strukturell kompatibel mit
        // GetBookingsByCourseIdWithCourseLessonBookings fuer die hier benoetigten Felder.
        // Type-Cast analog zu MoreMenuSettlementDetails.tsx:35.
        bookingsArray = [booking] as unknown as Parameters<typeof prepareInitialValues>[2];
        initialValues = prepareInitialValues(initialCourseValues as unknown as Parameters<typeof prepareInitialValues>[0], latestPriceData, bookingsArray) as unknown as PossibleFormikPropsType;
    }

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {i18n.containers.invoice.SettlementDetails.adjustModal.cancel()}
                </Button>,
                <Button key="ok" type="primary" loading={mutationLoading} onClick={handleConfirm}>
                    {i18n.containers.invoice.SettlementDetails.adjustModal.confirm()}
                </Button>,
            ]}
            width="80%"
            style={{top: 60}}
            bodyStyle={{height: "75vh", paddingTop: 0, overflowY: "auto"}}
            destroyOnClose
        >
            {loading || !data || !initialValues ? (
                <LoadingContent />
            ) : error ? (
                <div>{`Fehler beim Laden des Kurses: ${error.message}`}</div>
            ) : courseId ? (
                <Formik initialValues={initialValues} onSubmit={() => undefined} enableReinitialize={false}>
                    {(formikProps) => {
                        formikRef.current = formikProps as FormikProps<PossibleFormikPropsType>;

                        return (
                            <ModalContainer
                                courseId={courseId}
                                formikProps={formikProps as FormikProps<PossibleFormikPropsType>}
                                bookingsOfCourse={bookingsArray}
                                hideSelector={true}
                            />
                        );
                    }}
                </Formik>
            ) : (
                <div>{"Keine Kurs-ID verfügbar."}</div>
            )}
        </Modal>
    );
};

export default LessonBookingAdjustmentModal;
